import React, {
  createContext,
  useContext,
  ChangeEvent,
  useState,
  KeyboardEvent
} from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import {
  createBXPMediaResourceGroup,
  updateBXPMediaResourceGroup,
  deleteBXPMediaResourceGroup,
  BXPMediaResourceGroup
} from 'services/hvaservicecampaign.service';

interface MediaResourceGroupsTableProps {
  mediaResourceGroups: BXPMediaResourceGroup[];
  onMediaResourceGroupsChange: () => Promise<void>;
}

const MediaResourceGroupsChangeContext = createContext<
  (() => Promise<void>) | undefined
>(undefined);

const useMediaResourceGroupsChange = () => {
  const context = useContext(MediaResourceGroupsChangeContext);
  if (context === undefined) {
    throw new Error(
      'useMediaResourceGroupsChange must be used within a MediaResourceGroupsChangeProvider'
    );
  }
  return context;
};

type MediaResourceGroupActionCellProps = {
  mediaResourceGroup: BXPMediaResourceGroup;
};

const MediaResourceGroupActionCell = ({
  mediaResourceGroup
}: MediaResourceGroupActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMediaResourceGroup, setSelectedMediaResourceGroup] =
    useState<BXPMediaResourceGroup | null>(null);
  const onMediaResourceGroupsChange = useMediaResourceGroupsChange();

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedMediaResourceGroup(null);
  };

  const handleShowEditModal = () => {
    setSelectedMediaResourceGroup(mediaResourceGroup);
    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteBXPMediaResourceGroup(mediaResourceGroup.id);
      console.log(
        `Media Resource Group with ID: ${mediaResourceGroup.id} deleted successfully`
      );
      await onMediaResourceGroupsChange();
    } catch (error) {
      console.error(
        `Error deleting Media Resource Group with ID: ${mediaResourceGroup.id}`,
        error
      );
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedMediaResourceGroup) {
      try {
        await updateBXPMediaResourceGroup(
          selectedMediaResourceGroup.id,
          selectedMediaResourceGroup
        );
        await onMediaResourceGroupsChange();
        handleCloseEditModal();
      } catch (error) {
        alert('Error updating media resource group: ' + error);
      }
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }
    // eslint-disable-next-line
    setSelectedMediaResourceGroup(prev => (prev ? { ...prev, [name]: newValue } : null));
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faEdit} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={handleShowDeleteModal} className="text-danger">
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this Media Resource Group?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Update Media Resource Group</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={selectedMediaResourceGroup?.shortname || ''}
                onChange={handleInputChange}
                required
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Short Description</Form.Label>
              <Form.Control
                type="text"
                name="shortdescribe"
                value={selectedMediaResourceGroup?.shortdescribe || ''}
                onChange={handleInputChange}
                maxLength={64}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Rotate Mode</Form.Label>
              <Form.Select
                name="rotatemode"
                value={selectedMediaResourceGroup?.rotatemode || 'none'}
                onChange={handleInputChange}
              >
                <option value="none">none</option>
                <option value="lineal">lineal</option>
                <option value="rmemory">rmemory</option>
                <option value="random">random</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={selectedMediaResourceGroup?.active || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Media Resource Group
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const mediaResourceGroupsTableColumns: ColumnDef<BXPMediaResourceGroup>[] =
  [
    {
      accessorKey: 'id',
      header: 'ID',
      meta: {
        headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'shortname',
      header: 'Short Name',
      meta: {
        headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'action',
      enableSorting: false,
      header: '',
      cell: ({ row: { original } }) => {
        return <MediaResourceGroupActionCell mediaResourceGroup={original} />;
      },
      meta: {
        cellProps: { className: 'text-body-tertiary' }
      }
    },
    {
      accessorKey: 'shortdescribe',
      header: 'Description',
      meta: {
        headerProps: { style: { width: '25%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1' }
      }
    },
    {
      accessorKey: 'rotatemode',
      header: 'Rotate Mode',
      meta: {
        headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'active',
      header: 'Active',
      cell: ({ row }) => (row.original.active ? 'Yes' : 'No'),
      meta: {
        headerProps: { style: { width: '45%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap' }
      }
    }
  ];

export const BXPMediaResourceGroupsTable: React.FC<
  MediaResourceGroupsTableProps
> = ({ mediaResourceGroups, onMediaResourceGroupsChange }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMediaResourceGroup, setNewMediaResourceGroup] =
    useState<BXPMediaResourceGroup>({
      id: 0,
      shortname: '',
      shortdescribe: '',
      rotatemode: 'none',
      active: false
    });
  const table = useAdvanceTable({
    data: mediaResourceGroups,
    columns: mediaResourceGroupsTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }

    setNewMediaResourceGroup(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await createBXPMediaResourceGroup(newMediaResourceGroup);
      handleCloseAddModal();
      onMediaResourceGroupsChange();
    } catch (error) {
      console.error('Error creating media resource group:', error);
    }
  };

  return (
    <MediaResourceGroupsChangeContext.Provider
      value={onMediaResourceGroupsChange}
    >
      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Search Media Resource Groups"
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyPress}
            />
            <div className="ms-auto">
              <Button
                variant="primary"
                className="p-2"
                onClick={handleShowAddModal}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                Add Media Resource Group
              </Button>
            </div>
          </div>
        </div>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Media Resource Group</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={newMediaResourceGroup.shortname}
                onChange={handleInputChange}
                required
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Short Description</Form.Label>
              <Form.Control
                type="text"
                name="shortdescribe"
                value={newMediaResourceGroup.shortdescribe}
                onChange={handleInputChange}
                maxLength={64}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Rotate Mode</Form.Label>
              <Form.Select
                name="rotatemode"
                value={newMediaResourceGroup.rotatemode || 'none'}
                onChange={handleInputChange}
                defaultValue="none"
              >
                <option value="none">none</option>
                <option value="lineal">lineal</option>
                <option value="rmemory">rmemory</option>
                <option value="random">random</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={newMediaResourceGroup.active}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAddModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Media Resource Group
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </MediaResourceGroupsChangeContext.Provider>
  );
};
